import { useState, useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { PlugZap, Search, SearchX, ShieldCheck } from 'lucide-react';
import { useBusinessUnit, useIntegrations } from '@/hooks';
import { IntegrationCard, INTEGRATIONS, mergeIntegrationData } from '@/modules/integrations';
import { CustomToolsLink } from '@/modules/integrations/custom-tools';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { isAccountScopedIntegration, isKimHelpdesk } from '@/modules/integrations/provider-kind';
import type { CatalogIntegration } from '@/modules/integrations/types';

/**
 * Two tiers, in the order they are set up. The helpdesk comes first, on its own: one helpdesk
 * serves every brand BY DESIGN — brands are told apart inside it by the inbox a ticket arrives
 * on — so it is not "brand X's" and an account connects exactly one primary, with Kim Helpdesk
 * always beside it as the secondary. Brand integrations follow,
 * store & orders first: two brands genuinely run two Shopify stores, and each connection
 * belongs to the brand selected in the switcher.
 */
const BRAND_SECTIONS: { label: string; tags: string[] }[] = [
	{ label: 'Store & orders', tags: ['order-management'] },
	{ label: 'Subscriptions', tags: ['subscription'] },
	{ label: 'Shipping & tracking', tags: ['ecommerce'] },
	{ label: 'Messaging', tags: ['messaging'] },
	{ label: 'Social', tags: ['social'] },
	{ label: 'Reviews & Loyalty', tags: ['reviews'] },
	{ label: 'Knowledge', tags: ['knowledge'] }
];

type Section = { label: string; items: CatalogIntegration[] };

function groupBrandIntegrations(integrations: CatalogIntegration[]): Section[] {
	return BRAND_SECTIONS.map(({ label, tags }) => ({
		label,
		items: integrations.filter((i) => i.tags.some((t) => tags.includes(t)))
	})).filter((section) => section.items.length > 0);
}

const TABS = [
	{ value: 'all', label: 'All' },
	{ value: 'connected', label: 'Connected' },
	{ value: 'not-connected', label: 'Not connected' }
] as const;
type Tab = (typeof TABS)[number]['value'];

// Same underline tabs as the Automation and Operating Hours settings headers.
const TAB_TRIGGER = cn(
	'h-10 flex-none gap-1.5 px-0 py-[11px] text-[13px] font-normal leading-[18px] bg-transparent rounded-none border-0 shadow-none text-gray-800 transition-colors',
	'data-[state=active]:text-ink-strong data-[state=active]:font-medium data-[state=active]:border-b-2 data-[state=active]:border-ink-strong data-[state=active]:drop-shadow-none'
);

function matchesTab(integration: CatalogIntegration, tab: Tab): boolean {
	if (tab === 'connected') return integration.isConnected;
	if (tab === 'not-connected') return !integration.isConnected;
	return true;
}

export default function Integrations() {
	const [search, setSearch] = useState('');
	// In the URL so a link can land on a tab and Back returns to it.
	const [params, setParams] = useSearchParams();
	const tab: Tab = TABS.some((t) => t.value === params.get('tab')) ? (params.get('tab') as Tab) : 'all';
	const setTab = (next: Tab) =>
		setParams(
			(prev) => {
				if (next === 'all') prev.delete('tab');
				else prev.set('tab', next);
				return prev;
			},
			{ replace: true }
		);
	const { data: apiIntegrations, isLoading, error } = useIntegrations();

	// Google is hidden from this page for now (still used elsewhere, e.g. SOP editor
	// logos). Filtered by name, not id — id is overwritten with the backend account
	// id once connected. Kept inside the memo from this branch.
	const integrations = useMemo(
		() => mergeIntegrationData(INTEGRATIONS, apiIntegrations ?? []).filter((i) => i.name.toLowerCase() !== 'google'),
		[apiIntegrations]
	);

	const filtered = useMemo(() => {
		if (!search.trim()) return integrations;
		const q = search.toLowerCase();
		return integrations.filter((i) => i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
	}, [integrations, search]);

	const counts = useMemo(
		() =>
			Object.fromEntries(TABS.map((t) => [t.value, filtered.filter((i) => matchesTab(i, t.value)).length])) as Record<
				Tab,
				number
			>,
		[filtered]
	);
	const visible = useMemo(() => filtered.filter((i) => matchesTab(i, tab)), [filtered, tab]);
	const brandSections = useMemo(
		() => groupBrandIntegrations(visible.filter((i) => !isAccountScopedIntegration(i.catalogId))),
		[visible]
	);
	// Connected one first: it is the account's helpdesk, the rest are the alternatives.
	const helpdesks = useMemo(
		() =>
			visible
				.filter((i) => isAccountScopedIntegration(i.catalogId))
				.sort((a, b) => Number(b.isConnected) - Number(a.isConnected)),
		[visible]
	);
	// The primary is the one external helpdesk; Kim Helpdesk is always the secondary beside it.
	const primaryHelpdesks = helpdesks.filter((i) => !isKimHelpdesk(i.catalogId));
	const kimHelpdesk = helpdesks.find((i) => isKimHelpdesk(i.catalogId));
	const connectedHelpdesk = integrations.find(
		(i) => isAccountScopedIntegration(i.catalogId) && !isKimHelpdesk(i.catalogId) && i.isConnected
	)?.name;
	const { buId, businessUnits } = useBusinessUnit();
	const brandName = businessUnits.length > 1 ? businessUnits.find((b) => b.id === buId)?.name : undefined;

	if (error) {
		return (
			<div className='flex flex-col gap-6 p-6'>
				<h1 className='text-base font-semibold'>Integrations</h1>
				<p className='text-sm text-destructive'>Failed to load integrations. Please try again later.</p>
			</div>
		);
	}

	return (
		<div className='flex flex-col h-full'>
			{/* Page header */}
			<div className='sticky top-0 z-10 bg-white flex flex-col gap-2 px-4 pt-4 border-b border-gray-500'>
				<div className='flex items-center justify-between'>
					<h1 className='text-base font-semibold'>Integrations</h1>
					<div className='flex items-center gap-2'>
						<CustomToolsLink />
						<Button asChild variant='outline' className='h-8 gap-1 rounded-full border-gray-500 text-xs font-normal'>
							<Link to='/settings/integrations/preconditions'>
								<ShieldCheck size={14} />
								Account-wide preconditions
							</Link>
						</Button>
						<div className='relative'>
							<Search className='absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none' />
							<Input
								type='search'
								name='integrations-search'
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder='Search'
								// This is a filter box, not a credential field. Without these, a password
								// manager treats an unnamed text input near the connect form's password/
								// API-key field as the "username" and autofills it on connect. autoComplete
								// alone is ignored by 1Password/LastPass, hence their vendor opt-out attrs.
								autoComplete='off'
								data-1p-ignore
								data-lpignore='true'
								className='pl-8 h-8 w-[200px] rounded-full border-gray-500 text-xs'
							/>
						</div>
					</div>
				</div>
				<Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
					<TabsList className='flex gap-5 bg-transparent p-0 h-auto border-0 rounded-none justify-start'>
						{TABS.map((t) => (
							<TabsTrigger key={t.value} value={t.value} className={TAB_TRIGGER}>
								{t.label}
								{!isLoading && (
									<span
										className={cn(
											'min-w-5 rounded-full px-1.5 text-[11px] leading-[18px] tabular-nums',
											tab === t.value ? 'bg-ink-strong text-white' : 'bg-gray-100 text-gray-900'
										)}
									>
										{counts[t.value]}
									</span>
								)}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>

			{/* Content */}
			<div className='flex flex-col gap-10 p-4 overflow-auto'>
				{isLoading ? (
					<div className='flex flex-col gap-6'>
						<Skeleton className='h-4 w-20' />
						<div className='grid grid-cols-3 gap-4'>
							{[1, 2, 3].map((i) => (
								<div key={i} className='flex flex-col gap-3 p-4 border rounded-2xl'>
									<Skeleton className='h-10 w-10 rounded-[11px]' />
									<Skeleton className='h-4 w-28' />
									<Skeleton className='h-3 w-full' />
									<div className='flex justify-end'>
										<Skeleton className='h-7 w-20 rounded-full' />
									</div>
								</div>
							))}
						</div>
					</div>
				) : brandSections.length === 0 && helpdesks.length === 0 ? (
					<EmptyState search={search.trim()} tab={tab} onBrowse={() => setTab('not-connected')} />
				) : (
					<>
						{helpdesks.length > 0 && (
							<Tier
								title='Helpdesk'
								badge='Account-wide'
								description='One primary helpdesk for the whole account, with Kim Helpdesk beside it as the secondary. Both serve every brand; tickets are matched to a brand by the inbox they arrive on.'
							>
								{primaryHelpdesks.length > 0 && (
									<div className='flex flex-col gap-3'>
										<p className='text-xs font-medium text-muted-foreground'>Primary helpdesk</p>
										<HelpdeskTier helpdesks={primaryHelpdesks} connectedHelpdesk={connectedHelpdesk} searching={!!search.trim()} />
									</div>
								)}
								{kimHelpdesk && (
									<div className='flex flex-col gap-3'>
										<p className='text-xs font-medium text-muted-foreground'>
											Secondary helpdesk · always Kim Helpdesk, e.g. for live chat next to your primary
										</p>
										<CardGrid items={[kimHelpdesk]} />
									</div>
								)}
							</Tier>
						)}
						{brandSections.length > 0 && (
							<Tier
								title='Brand integrations'
								badge={brandName}
								description={
									brandName
										? `Connected for ${brandName} only. Switch brand to set up another brand's store, subscriptions and shipping.`
										: 'Your store, subscriptions, shipping and other tools. Each connection belongs to a brand.'
								}
							>
								{brandSections.map((section) => (
									<div key={section.label} className='flex flex-col gap-3'>
										<p className='text-xs font-medium text-muted-foreground'>{section.label}</p>
										<CardGrid items={section.items} />
									</div>
								))}
							</Tier>
						)}
					</>
				)}
			</div>
		</div>
	);
}

function Tier({
	title,
	badge,
	description,
	children
}: {
	title: string;
	badge?: string;
	description: string;
	children: ReactNode;
}) {
	return (
		<section className='flex flex-col gap-4'>
			<div className='flex flex-col gap-1 border-b border-gray-500 pb-3'>
				<div className='flex items-center gap-2'>
					<h2 className='text-sm font-semibold text-ink'>{title}</h2>
					{badge && (
						<span className='rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700'>{badge}</span>
					)}
				</div>
				<p className='text-xs text-muted-foreground'>{description}</p>
			</div>
			{children}
		</section>
	);
}

/**
 * With a helpdesk connected the alternatives can't be connected anyway, so they fold into one
 * card of logos beside it — the brand integrations below get the room. A search still shows
 * the full cards it matches.
 *
 * Every card stays mounted in the one grid whether folded or not; folding only hides them. A
 * card owns its connect/manage sheet, so swapping the tree when a helpdesk connects (or is
 * disconnected from its sheet) would remount it and close that sheet mid-flow.
 */
export function HelpdeskTier({
	helpdesks,
	connectedHelpdesk,
	searching
}: {
	helpdesks: CatalogIntegration[];
	connectedHelpdesk?: string;
	searching: boolean;
}) {
	const [showOthers, setShowOthers] = useState(false);
	const connected = helpdesks.filter((i) => i.isConnected);
	const others = helpdesks.filter((i) => !i.isConnected);
	const folded = !!connectedHelpdesk && !searching && connected.length > 0;

	const card = (integration: CatalogIntegration, hidden = false) => (
		<div key={cardKey(integration)} data-hidden={hidden || undefined} className={hidden ? 'hidden' : 'contents'}>
			<IntegrationCard integration={integration} connectedHelpdesk={connectedHelpdesk} />
		</div>
	);

	return (
		<div className='grid grid-cols-3 gap-4'>
			{folded
				? [
						...connected.map((i) => card(i)),
						others.length > 0 && (
							<div
								key='one-helpdesk'
								className='col-span-2 flex flex-col justify-between gap-3 rounded-2xl border border-dashed border-gray-500 bg-gray-50 p-4'
							>
								<div className='flex flex-col gap-1'>
									<p className='text-sm font-medium text-ink'>One primary helpdesk at a time</p>
									<p className='text-[13px] leading-[18px] text-muted-foreground'>
										{connectedHelpdesk} is your primary helpdesk and serves every brand. To use a different one, disconnect{' '}
										{connectedHelpdesk} first. Kim Helpdesk can still run beside it as the secondary.
									</p>
								</div>
								<div className='flex items-center justify-between gap-3'>
									<div className='flex items-center gap-1.5' aria-label='Other helpdesks'>
										{others.map((i) => (
											<img
												key={i.name}
												src={i.logo}
												alt={i.name}
												title={i.name}
												className='size-7 rounded-md border border-gray-500 bg-white object-contain p-0.5'
											/>
										))}
									</div>
									<Button variant='outline' size='sm' className='h-7 rounded-full text-xs' onClick={() => setShowOthers((v) => !v)}>
										{showOthers ? 'Hide other helpdesks' : `Show other helpdesks (${others.length})`}
									</Button>
								</div>
							</div>
						),
						...others.map((i) => card(i, !showOthers))
					]
				: helpdesks.map((i) => card(i))}
		</div>
	);
}

// Key by name AND brand. The name alone is still the stable half: integration.id flips from the
// catalog id to the real DB id on connect, and an id-based key would remount the card — resetting
// its open sheet and wizard step mid-connect. The brand is the added dimension, because one
// account can hold two connections for the same provider.
const cardKey = (integration: CatalogIntegration) => `${integration.name}:${integration.buId ?? 'none'}`;

function CardGrid({ items }: { items: CatalogIntegration[] }) {
	return (
		<div className='grid grid-cols-3 gap-4'>
			{items.map((integration) => (
				<IntegrationCard key={cardKey(integration)} integration={integration} />
			))}
		</div>
	);
}

function EmptyState({ search, tab, onBrowse }: { search: string; tab: Tab; onBrowse: () => void }) {
	const noConnections = !search && tab === 'connected';
	const Icon = noConnections ? PlugZap : SearchX;
	const title = search
		? `No integrations match "${search}"`
		: noConnections
			? 'No integrations connected yet'
			: 'Everything is connected';
	const body = search
		? 'Try a different name, or check the other tabs.'
		: noConnections
			? 'Connect your helpdesk, store and other tools so Kim can read and act on them.'
			: 'Every available integration is already connected.';

	return (
		<div className='flex flex-col items-center gap-3 py-20 text-center'>
			<div className='flex size-10 items-center justify-center rounded-full bg-gray-100'>
				<Icon className='size-5 text-gray-900' />
			</div>
			<div className='flex flex-col gap-1'>
				<p className='text-sm font-medium text-ink'>{title}</p>
				<p className='text-xs text-muted-foreground'>{body}</p>
			</div>
			{noConnections && (
				<Button variant='outline' className='h-8 rounded-full text-[13px] font-normal' onClick={onBrowse}>
					Browse integrations
				</Button>
			)}
		</div>
	);
}
